import React, { useEffect, useState, useRef} from "react";
import { useSelector , useDispatch} from "react-redux";
import { fetchAssetSubtitle, invalidateSubtitleUpdate, updateSubtitle } from "src/redux/actions/assets";

import Button from "src/components/button";
import { Asset } from "src/types/assets";

import _ from 'lodash';
import cx from "classnames";
import styles from "./videoCaption.module.scss";

interface VideoCaptionProps {
  asset: Asset;
  onClose: () => void;
}
interface SubtitleEntry {
  start?: string;
  end?: string;
  part?: string;
}

const VideoCaption: React.FC<VideoCaptionProps> = ({asset, onClose}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subtitle, setSubtitle] = useState<any[]>([]);

  const assetId = asset.id;
  const captions = useSelector((state: any) => _.get(state, 'asset.subtitle.items', []));
  const updating = useSelector((state: any) =>_.get(state, 'asset.subtitle.updating', false));
  const updated = useSelector((state: any) => _.get(state, 'asset.subtitle.updated', false));

  
  useEffect(() => {
    if(_.size(captions) === 0) {
      dispatch(fetchAssetSubtitle({ id : assetId }));
    }
  }, []);


  useEffect(() => {
    if (Array.isArray(captions)) { 
      setSubtitle(captions);
    } else {
      setSubtitle([]);
    }
  }, [captions]);

  useEffect(() => {
   if(updated) {
    onClose();
    dispatch(invalidateSubtitleUpdate())
   }
  }, [updated]);

  const handleSubtitleChange = (e, index) => {
    const updatedSubtitles = [...subtitle];
    updatedSubtitles[index] = {
      ...updatedSubtitles[index],
      part: e.target.value,
    };
    setSubtitle(updatedSubtitles);
  };

  const formatTimestamp = (timestamp) => {
    return timestamp.substring(3, 8); 
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const fileContent = event.target?.result;

        const subtitlesData = vttToJson(fileContent);
        setSubtitle(subtitlesData);
        
      };
      reader.readAsText(selectedFile);
    }
  };

  const vttToJson = (vttContent) => {
    const lines = vttContent.split('\n');
    const subtitlesArray: SubtitleEntry[] = [];
    let subtitleEntry: SubtitleEntry | null = null;

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine.toUpperCase() === 'WEBVTT') {
        continue;
      }

      const timestampMatch = trimmedLine.match(
        /(\d{2}:\d{2}:\d{2}\.\d{3}) --> (\d{2}:\d{2}:\d{2}\.\d{3})/
      );

      if (timestampMatch) {
        if (subtitleEntry) {
          subtitlesArray.push(subtitleEntry);
        }
        subtitleEntry = { start: timestampMatch[1], end: timestampMatch[2], part: '' };
      } else if (subtitleEntry) {
        subtitleEntry.part += (subtitleEntry.part ? ' ' : '') + trimmedLine;
      }
    }
  
    if (subtitleEntry) {
      subtitlesArray.push(subtitleEntry);
    }
    return subtitlesArray;
  };

  const openFileManager = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUpdate = () => {
    if (assetId && subtitle.length > 0) {
      dispatch(updateSubtitle({ id: assetId, subtitle: subtitle }));
    }
  };

  return (
    <section className={styles.container}>
      <div className={styles.captionsContainer}>
              <div className={styles.captionBody}>
        <div className={styles.captionLabels}>
          <div>Timestamp</div>
          <div>Captions</div>
          <div className={styles.uploadText}>
            <i className={cx("paperflite-upload", styles.uploadIcon)} />
            <div className={styles.upload} onClick={openFileManager}>
              Upload New VTT
            </div>
            <input
              type="file"
              accept=".vtt"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
        </div>
        <div className={styles.captions}>
          {subtitle.map((item, index) => (
            <div key={index} className={styles.assetItem}>
              <p
                className={styles.assetTimestamp}
              >{`${formatTimestamp(item.start)} - ${formatTimestamp(item.end)}`}</p>
              <textarea
                key={index}
                value={item.part}
                className={cx(styles.assetCaption)}
                style={{ resize: "none", outline: "none" }}
                rows={2}
                onChange={(e) => handleSubtitleChange(e, index)}
              />
            </div>
          ))}
        </div>
      </div>
      </div>
      <div className={styles.buttonContainer}>
        <Button
          variant="primary"
          className={styles.saveButton}
          icon="paperflite-tick"
          text={updating ? "Updating..." : "Update"}
          disabled={updating}
          onClick={() => handleUpdate()}
        />
        <Button
          variant="secondary"
          className={styles.cancelButton}
          onClick={() => onClose()}
          text="Cancel"
        />
      </div>
    </section>
  );
};

export default VideoCaption;
